import { Products } from 'enums';
import { UserInformationForm } from 'features/account/account-management/use-settings-form';
import { Token, User } from 'hooks/use-extension-auth';
import _ from 'lodash';
import { UserInfo } from 'types/user.types';
import type { License } from 'types/licenses.types';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import {
  initTokenRefresh,
  scheduleProactiveRefresh,
  clearProactiveRefresh,
  refreshTokenCall,
} from 'API/APIs/token-refresh';

interface UserStore {
  basics: User | undefined;
  details: UserInfo | undefined;
  products: Products[];
  token: Token | undefined;
  licenses: License[];

  promptOpen: boolean;

  setBasics: (newBasics: User | undefined) => void;

  setDetails: (newDetails: UserInfo | undefined) => void;

  setProducts: (newProducts: Products[]) => void;

  setToken: (token: Token | undefined) => void;

  setLicenses: (licenses: License[]) => void;

  clearLicenses: () => void;

  signIn: (basics: User, details: UserInfo, products: Products[], token: Token, licenses?: License[]) => void;

  signOut: () => void;

  saveUserInformationPersistent: (userInformation: UserInformationForm) => void;

  togglePrompt: (open: boolean) => void;
}

// Safe JSON parse helper – avoids crash on corrupt localStorage data
function safeParse<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    localStorage.removeItem(key);
    return fallback;
  }
}

const parsedBasics = safeParse<User | undefined>('user-basics', undefined);
const parsedDetails = safeParse<UserInfo | undefined>('user-details', undefined);
const parsedProducts = safeParse<Products[]>('user-products', []);
const parsedToken = safeParse<Token | undefined>('user-token', undefined);
const parsedLicenses = safeParse<License[]>('user-licenses', []);

const useUserStore = createWithEqualityFn<UserStore>(
  (set, get) => ({
    basics: parsedBasics,
    details: parsedDetails,
    products: parsedProducts,
    token: parsedToken,
    licenses: parsedLicenses,

    promptOpen: false,

    setBasics(newBasics) {
      const oldBasics = get().basics;
      if (!_.isEqual(oldBasics, newBasics)) {
        set(() => ({ basics: newBasics }));
        if (!_.isNil(newBasics)) {
          localStorage.setItem('user-basics', JSON.stringify(newBasics));
        } else {
          localStorage.removeItem('user-basics');
        }
      }
    },

    setDetails(newDetails) {
      const oldDetails = get().details;
      if (!_.isEqual(oldDetails, newDetails)) {
        set(() => ({ details: newDetails }));
        if (!_.isNil(newDetails)) {
          localStorage.setItem('user-details', JSON.stringify(newDetails));
        } else {
          localStorage.removeItem('user-details');
        }
      }
    },

    setProducts(newProducts) {
      const oldProducts = get().products;
      if (!_.isEqual(oldProducts, newProducts)) {
        set(() => ({ products: newProducts }));
        if (!_.isNil(newProducts) && newProducts.length > 0) {
          localStorage.setItem('user-products', JSON.stringify(newProducts));
        } else {
          localStorage.removeItem('user-products');
        }
      }
    },

    setToken(newToken) {
      const oldToken = get().token;
      if (!_.isEqual(oldToken, newToken)) {
        set(() => ({ token: newToken }));
        if (!_.isNil(newToken)) {
          localStorage.setItem('user-token', JSON.stringify(newToken));
          scheduleProactiveRefresh(newToken.expiresIn);
        } else {
          localStorage.removeItem('user-token');
          clearProactiveRefresh();
        }
      }
    },

    setLicenses(newLicenses) {
      const oldLicenses = get().licenses;
      if (!_.isEqual(oldLicenses, newLicenses)) {
        set(() => ({ licenses: newLicenses }));
        if (!_.isNil(newLicenses) && newLicenses.length > 0) {
          localStorage.setItem('user-licenses', JSON.stringify(newLicenses));
        } else {
          localStorage.removeItem('user-licenses');
        }
      }
    },

    clearLicenses() {
      set(() => ({ licenses: [] }));
      localStorage.removeItem('user-licenses');
    },

    signIn(basics, details, products, token, licenses) {
      const state = get();

      state.setBasics(basics);
      state.setDetails(details);
      state.setProducts(products);
      state.setToken(token);
      if (licenses) {
        state.setLicenses(licenses);
      }
    },

    signOut() {
      const state = get();

      clearProactiveRefresh();

      state.setBasics(undefined);
      state.setDetails(undefined);
      state.setProducts([]);
      state.setToken(undefined);
      state.setLicenses([]);

      set(() => ({ promptOpen: false }));
    },

    saveUserInformationPersistent(userInformation: UserInformationForm) {
      const state = get();
      const updateDetails = { ...state.details };

      if (!_.isNil(updateDetails)) {
        updateDetails.firstName = userInformation.firstName;
        updateDetails.lastName = userInformation.lastName;
        updateDetails.email = userInformation.email;
        updateDetails.language = userInformation.language;

        state.setDetails(updateDetails as UserInfo);

        localStorage.setItem('user-details', JSON.stringify(updateDetails));
      }
    },

    togglePrompt(open: boolean) {
      const oldOpen = get().promptOpen;
      if (!_.isEqual(oldOpen, open)) {
        set(() => ({ promptOpen: open }));
      }
    },
  }),
  shallow
);

// ---------------------------------------------------------------------------
// Wire up the token-refresh service with store callbacks
// ---------------------------------------------------------------------------
initTokenRefresh(
  () => useUserStore.getState().token?.refreshToken,
  (data) => useUserStore.getState().setToken(data),
);

// ---------------------------------------------------------------------------
// Bootstrap: resume the proactive refresh timer on page load / tab wake-up
// ---------------------------------------------------------------------------

if (parsedToken?.refreshToken) {
  const storedTtl = typeof parsedToken.expiresIn === 'number'
    ? parsedToken.expiresIn
    : typeof parsedToken.expiresIn === 'string'
      ? parseInt(parsedToken.expiresIn as string, 10) || 3600
      : 3600;
  // Token age is unknown on reload; use half the TTL for a conservative estimate
  scheduleProactiveRefresh(Math.floor(storedTtl / 2));
}

// When the user returns to the tab after it was sleeping, do an immediate
// refresh so the token is guaranteed fresh for the next API call.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      const token = useUserStore.getState().token;
      if (token?.refreshToken) {
        refreshTokenCall().then((res) => {
          if (!res.error) {
            scheduleProactiveRefresh(res.expiresIn);
          }
        });
      }
    }
  });
}

export default useUserStore;
